import { formatAmount, toBaseUnits, type TokenScale } from "@/lib/format/amount";
import { amountError, parseTypedAmount } from "./amount";
import { WalletError } from "./types";

/* Paying a deposit from the connected browser wallet. The amount is in the
   route's `fromToken` — the token that moves on chain — at its `decimals`, and
   the rules and messages are the whitelabel's (EvmDepositContent +
   amountValidation). */

export type DepositLimits = {
  /* The route's minimum, base units of the deposit token. */
  minimumAmount: string;
  token: TokenScale & { symbol: string };
  /* The connected wallet's holding of the token, when one is connected and it
     could be read. An amount above it is refused here, before the wallet is
     asked to sign a transfer it cannot fund. */
  balance?: bigint | null;
};

export type DepositErrors = { amount: string | null };

export const TRANSFER_STEP = 100n;

export const transferAmount = (parsed: bigint) => parsed - (parsed % TRANSFER_STEP);

/* A route that states no minimum still takes no less than one whole token from
   a wallet payment — the whitelabel's floor, since a fraction of a token is
   not worth the gas of the transfer. The address itself, sent to by hand,
   keeps the route's own minimum. */
export const effectiveMinimum = (minimumAmount: string, decimals: number) => {
  const minimum = toBaseUnits(minimumAmount);

  return minimum === 0n ? 10n ** BigInt(decimals) : minimum;
};

export const validateDeposit = (
  { amount }: { amount: string },
  { minimumAmount, token, balance }: DepositLimits,
): DepositErrors => {
  const error = amountError(amount, {
    minAmount: effectiveMinimum(minimumAmount, token.decimals),
    maxAmount: balance ?? null,
    decimals: token.decimals,
    significantDecimals: token.significantDecimals,
    actionLabel: "deposit",
  });

  if (error !== null) {
    return { amount: error };
  }

  /* The send is truncated to whole multiples of TRANSFER_STEP; on a token
     with few decimals that can leave nothing to move. */
  const parsed = parseTypedAmount(amount, token.decimals);

  if (parsed !== null && transferAmount(parsed) <= 0n) {
    return { amount: "Amount must be greater than zero" };
  }

  return { amount: null };
};

/* An empty field is not an error to show, but it is not a deposit either. */
export const canDeposit = ({ amount }: { amount: string }, errors: DepositErrors) =>
  amount.trim().length > 0 && errors.amount === null;

/* The most the connected wallet can send, as the amount field takes it: the
   balance to the token's shown decimals, truncated — never rounded up past what
   is there — and ungrouped so it parses back. */
export const walletMaxAmount = (balance: bigint, token: DepositLimits["token"]) =>
  formatAmount(balance.toString(), token, { grouped: false });

/* The address the player paid from is already registered to another account
   (or used for withdrawals): `POST /wallets` answers 409 and the platform will
   not credit a deposit from it here. */
export class DepositAddressConflictError extends Error {
  constructor() {
    super("conflict");
    this.name = "DepositAddressConflictError";
  }
}

export const DEPOSIT_CONFLICT_MESSAGE =
  "Please use a different address that is not used for another account or for withdrawals.";

/* What a failed wallet payment says, as the whitelabel's EvmDepositContent
   words it: the conflict is named, a request the player turned down in the
   wallet is not an error at all (null), and anything the platform refused is
   "Deposit failed!". A wallet that could not do what was asked — no account
   shared, the chain not supported — keeps its own explanation, which tells the
   player more than the whitelabel's catch-all. */
export const depositFailureMessage = (error: unknown): string | null => {
  if (error instanceof DepositAddressConflictError) {
    return DEPOSIT_CONFLICT_MESSAGE;
  }

  if (error instanceof WalletError) {
    return error.kind === "rejected" ? null : error.message;
  }

  return "Deposit failed!";
};
